import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { QcResult, QcLot } from '@alpha-edge/shared'
import {
  PageHeader,
  DataTable,
  StatusBadge,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

interface QcReviewRow extends QcResult {
  readonly lotNumber: string
  readonly analyte: string
  readonly targetValue: number
  readonly targetSd: number
  readonly unit: string | null
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function flagLabel(sd: number): string {
  if (Math.abs(sd) > 3) return 'OUT'
  if (Math.abs(sd) > 2) return sd > 0 ? 'HIGH' : 'LOW'
  if (Math.abs(sd) > 1) return sd > 0 ? 'high' : 'low'
  return 'OK'
}

function flagStatus(sd: number): string {
  if (Math.abs(sd) > 3) return 'critical'
  if (Math.abs(sd) > 2) return 'warning'
  return 'active'
}

export function QcReviewPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<readonly QcReviewRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const { tableId: _, ...tableColumnProps } = useTableColumns('qc-review')

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.get<readonly QcLot[]>('/qc/lots?limit=100'),
      api.get<readonly QcResult[]>('/qc/results?limit=100'),
    ])
      .then(([lotsRes, resultsRes]) => {
        if (cancelled) return
        const lots = lotsRes.data ?? []
        const results = resultsRes.data ?? []
        const lotMap = new Map(lots.map((l) => [l.id, l]))

        const enriched: QcReviewRow[] = results
          .filter((r) => {
            const today = new Date().toISOString().split('T')[0]
            return r.performedAt.startsWith(today)
          })
          .map((r) => {
            const lot = lotMap.get(r.lotId)
            return {
              ...r,
              lotNumber: lot?.lotNumber ?? '--',
              analyte: lot?.analyte ?? '--',
              targetValue: lot?.targetValue ?? 0,
              targetSd: lot?.targetSd ?? 1,
              unit: lot?.unit ?? null,
            }
          })
          .sort((a, b) => {
            if (a.status === 'fail' && b.status !== 'fail') return -1
            if (b.status === 'fail' && a.status !== 'fail') return 1
            if (a.status === 'warning' && b.status === 'pass') return -1
            if (b.status === 'warning' && a.status === 'pass') return 1
            return 0
          })

        setRows(enriched)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load QC data')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  function handleReview(resultId: string, action: 'accepted' | 'rejected') {
    setActionLoading(resultId)
    setError(null)
    api
      .put(`/qc/results/${resultId}`, { reviewAction: action })
      .then(() => {
        setRows((prev) =>
          prev.map((r) =>
            r.id === resultId ? { ...r, reviewAction: action, reviewedAt: new Date().toISOString() } : r,
          ),
        )
        setActionLoading(null)
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Review failed')
        setActionLoading(null)
      })
  }

  const failCount = rows.filter((r) => r.status === 'fail').length
  const warningCount = rows.filter((r) => r.status === 'warning').length
  const needsReview = rows.filter((r) => (r.status === 'fail' || r.status === 'warning') && !r.reviewAction).length

  const columns: readonly Column<QcReviewRow>[] = [
    {
      key: 'lotNumber',
      header: 'Lot',
      sortable: true,
      filterable: true,
      render: (r) => <strong>{r.lotNumber}</strong>,
    },
    { key: 'analyte', header: 'Analyte', sortable: true, filterable: true, render: (r) => r.analyte },
    {
      key: 'value',
      header: 'Value',
      sortable: true,
      render: (r) => `${r.measuredValue}${r.unit ? ` ${r.unit}` : ''}`,
      sortValue: (r) => r.measuredValue,
    },
    {
      key: 'target',
      header: 'Target',
      render: (r) => `${r.targetValue} +/- ${r.targetSd}`,
    },
    {
      key: 'sd',
      header: 'SD',
      sortable: true,
      render: (r) => r.sdFromMean.toFixed(2),
      sortValue: (r) => Math.abs(r.sdFromMean),
    },
    {
      key: 'flag',
      header: 'Flag',
      sortable: true,
      render: (r) => <StatusBadge status={flagStatus(r.sdFromMean)} label={flagLabel(r.sdFromMean)} />,
    },
    {
      key: 'violations',
      header: 'Westgard',
      render: (r) =>
        r.westgardViolations && r.westgardViolations.length > 0 ? (
          <span style={{ color: colors.danger, fontWeight: 600, fontSize: '0.8125rem' }}>
            {r.westgardViolations.join(', ')}
          </span>
        ) : (
          '--'
        ),
    },
    {
      key: 'time',
      header: 'Time',
      sortable: true,
      render: (r) => formatTime(r.performedAt),
      sortValue: (r) => new Date(r.performedAt).getTime(),
    },
    {
      key: 'action',
      header: '',
      width: '100px',
      render: (r) => {
        if (r.reviewAction) {
          return <StatusBadge status={r.reviewAction === 'accepted' ? 'active' : 'retired'} label={r.reviewAction} />
        }
        if (r.status === 'pass') return null
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleReview(r.id, 'accepted')
            }}
            disabled={actionLoading === r.id}
            style={{ ...btnSecondary, padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
          >
            {actionLoading === r.id ? '...' : 'Accept'}
          </button>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="QC Review"
        subtitle={`Today: ${rows.length} runs, ${failCount} failures, ${warningCount} warnings`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={() => navigate('/qc/lots')} style={btnSecondary}>
              Lots
            </button>
            <button type="button" onClick={() => navigate('/qc/levey-jennings')} style={btnPrimary}>
              Charts
            </button>
          </div>
        }
      />

      {needsReview > 0 && (
        <div
          style={{
            padding: '0.6rem 1rem',
            marginBottom: '0.75rem',
            borderRadius: '6px',
            background: failCount > 0 ? '#fee2e2' : '#fef3c7',
            color: failCount > 0 ? '#dc2626' : '#92400e',
            border: `1px solid ${failCount > 0 ? '#fecaca' : '#fde68a'}`,
            fontSize: '0.9375rem',
            fontWeight: 500,
          }}
        >
          {needsReview} QC result{needsReview !== 1 ? 's' : ''} need{needsReview === 1 ? 's' : ''} review
        </div>
      )}

      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          keyField="id"
          onRowClick={(r) => navigate(`/qc/lots/${r.lotId}`)}
          emptyMessage="No QC runs recorded today."
          {...tableColumnProps}
        />
      )}
    </div>
  )
}
